import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "../../../../../../../../context/ChatContext/ChatContext";
import { Empty } from "antd";
import ChatRoomListItem from "../ChatRoomListItem";
import { useDispatch, useSelector, DefaultRootState } from "react-redux";
import { setChatSettings } from "../../../../../../../../appRedux/actions/ChatSettings";
import { v4 as uuidv4 } from "uuid";
import { createNewRoom, findExistingRoom } from "../../../../ChatScripts";
import { useIntl } from "react-intl";
import { FixedSizeList } from "react-window";

const ITEM_HEIGHT = 52;

const ChatUsersRoomList: React.FC = () => {
   const chatSettings = useSelector((state: DefaultRootState) => state.chatSettings);
   const { firstName, oldId } = useSelector((state: DefaultRootState) => state.user);

   const chatData = useChat();
   const intl = useIntl();
   const dispatch = useDispatch();

   const chatSettingsRef = useRef(chatSettings);
   chatSettingsRef.current = chatSettings;

   const containerRef = useRef<HTMLDivElement>(null);
   const [containerHeight, setContainerHeight] = useState(0);

   const users = useMemo(
      () => chatData?.chatData?.filteredPhpUsers?.filter((user) => user?.userType !== "bot") ?? [],
      [chatData?.chatData?.filteredPhpUsers],
   );

   useEffect(() => {
      if (!containerRef.current) return;
      const observer = new ResizeObserver(([entry]) => {
         setContainerHeight(entry.contentRect.height);
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
   }, [users.length]);

   const handleUserRoomClick = useCallback(
      (currentRoom) => {
         if (!currentRoom?.oldId || !oldId) return;

         const existingRoom = findExistingRoom(
            chatData?.chatData?.directRooms,
            oldId,
            currentRoom.oldId,
         );

         if (existingRoom) {
            dispatch(setChatSettings({ ...chatSettingsRef.current, currentRoom: existingRoom }));
         } else {
            const newRoom = createNewRoom(
               uuidv4(),
               "direct",
               undefined,
               [oldId, currentRoom.oldId],
               oldId,
               firstName,
               currentRoom.firstName,
            );
            dispatch(
               setChatSettings({
                  ...chatSettingsRef.current,
                  currentRoom: {
                     id: newRoom.id,
                     title: newRoom.title,
                     type: newRoom.type,
                     status: newRoom.status,
                     seenBy: newRoom.seenBy,
                     chatUsers: newRoom.chatUsers,
                  },
               }),
            );
         }
      },
      [oldId, firstName, chatData?.chatData?.directRooms, dispatch],
   );

   if (!users.length)
      return <Empty description={intl.formatMessage({ id: "chat.no-items-found" })} />;

   return (
      <div ref={containerRef} style={{ height: "100%" }}>
         {containerHeight > 0 && (
            <FixedSizeList
               height={containerHeight}
               itemCount={users.length}
               itemSize={ITEM_HEIGHT}
               width="100%"
               overscanCount={5}
            >
               {({ index, style }) => (
                  <div style={style}>
                     <ChatRoomListItem
                        listType="Contact"
                        user={users[index]}
                        onClick={handleUserRoomClick}
                        selectedRoom=""
                     />
                  </div>
               )}
            </FixedSizeList>
         )}
      </div>
   );
};

export default ChatUsersRoomList;
